import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { Hidden } from '@material-ui/core';
import Button from '@cpg-its/ads.common.button';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { NewHireProps } from '../../../models/arc-epi-props';
import { DividerSection } from '../../../../../../stories/divider-section';
import { getLetterMonthDate } from '../../../../../global/helpers/date-helpers';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { setCurrentNewHireBenefitEnrollmentStep } from '../slice/new-hire-enrollment-slices';
import { BenefitEnrollmentStep } from '../models/pension-enrollment-models';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { ArcRoutes } from '../../../constants/routes';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { PensionEnrollmentInfo, PensionEnrollmentPlan } from '../../pension-enrollment/models/pension-enrollment-model';
import { useMemberPensionEnrollmentInfo } from './hooks/useMemberPensionEnrollmentInfo';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { LabelAndValue } from '../../../../../global/components/data-display/label-value';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { shouldMemberNavigateCoverageStep, shouldMemberNavigateRetirementStep } from './benefit-enrollment/helper/member-benefit-enrollment-helper';
import { Employment } from '../../employment/employment-model';
import { MemberCompensation } from '../models/new-hire-enrollment-models';
import { MemberNewHireEnrollmentCoverage } from './models/member-coverage-model';

interface Props {	    
    basePath?: string;
}

export function MemberRetirementPlans(props: Props) {    	

	const app = useContext(DemographicsContext);
	const gpsItem = getGpsType(GpsType.MEMBER_NEW_HIRE_RETIREMENT_PLAN, app);
	
	const history = useHistory();
    const { basePath } = props;
    const enrollmentContext = useContext(AnnualEnrollmentContext);

    const newHirePropsLoaded = useSelector<TcsmStates, boolean>(state => state?.epiProps?.newHireSettingsState?.contentLoaded);
    const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
    
    const memberCurrentEmploymentIdLoaded = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.memberCurrentEmploymentIdLoaded);
    const memberPensionEnrollmentInfoLoaded = useMemberPensionEnrollmentInfo();

	const memberCurrentEmployerName = useSelector<TcsmStates, string>(state => state?.newHireEnrollmentState?.memberCurrentEmployer?.name);
	
	const memberEmployment = useSelector<TcsmStates, Employment>(state => state?.newHireEnrollmentState?.memberEmployment);
    const memberCompensationState = useSelector<TcsmStates, MemberCompensation>(state => state?.newHireEnrollmentState?.memberCompensation);	
	const memberCoverageState = useSelector<TcsmStates, MemberNewHireEnrollmentCoverage>(state => state.newHireEnrollmentState?.memberCoverageState);
    const memberPensionEnrollmentInfo = useSelector<TcsmStates, PensionEnrollmentInfo>(state => state?.newHireEnrollmentState?.memberPensionInfo);
	const enrolledPlans = memberPensionEnrollmentInfo?.enrolled_plans;	

	const canMemberNavigateCoverageStep = shouldMemberNavigateCoverageStep(memberCompensationState, memberEmployment, memberCoverageState);
	const canMemberNavigateRetirementStep = shouldMemberNavigateRetirementStep(memberPensionEnrollmentInfo, memberCompensationState, memberEmployment);

    const dispatch = useDispatch();

	const newHireEnrollmentRoot = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.MemberNewHireEnrollment}`);
	const memberNewHireEnrollmentBenefitEnrollmentRoot = `${newHireEnrollmentRoot}${NewHireEnrollmentRoutes.BenefitEnrollment}`;	
    const memberNewHireEnrollmentEmployeeRecordRoot = `${newHireEnrollmentRoot}${NewHireEnrollmentRoutes.EmployeeRecord}`;	

	const prevStep = `${memberNewHireEnrollmentEmployeeRecordRoot}${AccountInfoRoutes.EmploymentSegment}`;	
	const stepCoverage = `${memberNewHireEnrollmentBenefitEnrollmentRoot}${NewHireEnrollmentRoutes.Coverage}`;
	const stepReview = `${memberNewHireEnrollmentBenefitEnrollmentRoot}${NewHireEnrollmentRoutes.Review}`;

    useEffect(() => {		
		if (enrollmentContext === AnnualEnrollmentKey.newHireEnrollment)
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.RETIREMENT));
	}, []);

	function prevStepOnClick(){
		history.push(prevStep);
	}

	function nextStepOnClick(){
		if (enrollmentContext === AnnualEnrollmentKey.newHireEnrollment) {
			if (canMemberNavigateCoverageStep){
				history.push(stepCoverage);			
				return;
			}
			
			history.push(stepReview);			
		}
	}

    if (!memberCurrentEmploymentIdLoaded || !basePath){
        return <Redirect to={newHireEnrollmentRoot} />;
    }

	if (!memberPensionEnrollmentInfoLoaded || !memberPensionEnrollmentInfo || !newHirePropsLoaded) {
		return <LoadingCircle />;
	}

	function RenderContribution(enrolledPlan: PensionEnrollmentPlan){
		if (!enrolledPlan?.employee_contribution) return null;

		return (
			<ContentSpacer>
				<p><strong>{newHireProps?.contributionLabel}</strong></p>
				<p>{enrolledPlan?.employee_contribution}</p>
			</ContentSpacer>
		);
	}

	function RenderEmployerContributions(enrolledPlan: PensionEnrollmentPlan){
		if (!enrolledPlan?.employer_base && !enrolledPlan?.employer_match) return null;

		return (
			<>
				<LabelAndValue label={newHireProps?.employerBaseContributionLabel} tooltipMessage={newHireProps?.employerBaseContributionHelperText} value={ enrolledPlan?.employer_base || 0 } />
				<LabelAndValue label={newHireProps?.employerMatchContributionLabel} tooltipMessage={newHireProps?.employerMatchContributionHelperText} value={ enrolledPlan?.employer_match || 0 } />				
			</>
		);
	}

	if (!canMemberNavigateRetirementStep){
		nextStepOnClick();
	}

    return (
        <>
			{
				memberPensionEnrollmentInfo &&
				<>
					<PageHeader headerText={newHireProps?.retirementEnrollmentTitle} description={newHireProps?.retirementEnrollmentDescription} />
					<Hidden mdUp>
						<SideBarContent>
							<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
						</SideBarContent>
					</Hidden>
					<DividerSection>					
					{
						enrolledPlans?.length > 0 ?
						enrolledPlans?.map(enrolledPlan => {
							return (
								<ContentSpacer>
									<ContentSpacer>
										<h3>{enrolledPlan?.product?.description} - {enrolledPlan?.plan_id}</h3>
										<div>{memberCurrentEmployerName}</div>
									</ContentSpacer>
									{ RenderContribution(enrolledPlan) }
									{ RenderEmployerContributions(enrolledPlan) }											
									<ContentSpacer>
										<p><strong>{newHireProps?.effectiveDateLabel}</strong></p>
										<p>{getLetterMonthDate(enrolledPlan?.enrollment_effective_date)}</p>
									</ContentSpacer>
								</ContentSpacer>
							);
						}) :
						<WysiwygWithReactLink content={newHireProps?.noAvailablePlanMessage} />
                    }
					</DividerSection>
					<ButtonWrapper flexEnd>
						<Button type='button' buttonStyle='tertiary' text={newHireProps?.backToEmploymentCompButtonText} backgroundColor='#808080' onClick={prevStepOnClick} />
						<Button type='button' buttonStyle='primary' text={newHireProps?.continueToCoverageButtonText} backgroundColor='#0000FF' onClick={nextStepOnClick} />
					</ButtonWrapper>
				</>
			}
		</>
    );
}

MemberRetirementPlans.defaultProps = {
	basePath: null,
};
